import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Receipt, Check, Eye, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtCur = (v) => `Rs ${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
const fmtQty = (v) => (v || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const RunningBillGenerator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState(projectId || '');
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [step, setStep] = useState('preview'); // preview | confirm | done
  const [generatedBill, setGeneratedBill] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/projects?page=1&limit=200`, { withCredentials: true })
      .then(r => setProjects((r.data.projects || r.data).filter(p => p.status === 'Confirmed'))).catch(() => {});
  }, []);

  const fetchPreview = useCallback(async () => {
    if (!filterProject) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND}/api/projects/${filterProject}/running-bill-preview`, { withCredentials: true });
      setPreview(data);
      // Auto-select all lines with current_bill > 0
      setSelected(new Set(data.preview_lines.map((_, i) => i)));
      setStep('preview');
      setGeneratedBill(null);
    } catch { toast.error('Failed to load preview'); }
    finally { setLoading(false); }
  }, [filterProject]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const toggleLine = (idx) => {
    setSelected(s => { const n = new Set(s); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  };

  const selectedTotal = preview ? [...selected].reduce((s, i) => s + (preview.preview_lines[i]?.current_bill || 0), 0) : 0;

  const handleConfirm = () => setStep('confirm');

  const handleGenerate = async () => {
    if (!preview || selected.size === 0) return;
    setGenerating(true);
    try {
      const lines = [...selected].map(i => preview.preview_lines[i]);
      const { data } = await axios.post(`${BACKEND}/api/projects/${filterProject}/generate-running-bill`, {
        line_items: lines,
        quote_id: preview.quote_id,
        bill_number_hint: preview.bill_number_hint,
        description: preview.bill_number_hint,
      }, { withCredentials: true });
      toast.success(`Bill ${data.bill_number} generated`);
      setGeneratedBill(data);
      setStep('done');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate bill'); }
    finally { setGenerating(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate(-1)} variant="outline" className="border-zinc-300 rounded-none mb-3" data-testid="rb-back">
            <ArrowLeft size={20} className="mr-2" /> Back
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900" data-testid="rb-title">Generate Running Bill</h1>
          <p className="text-sm text-zinc-600 mt-1">Auto-calculate from BOQ progress + VO — preview and confirm</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-5">
          <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
            className="bg-white border border-zinc-200 rounded-none p-2.5 text-sm min-w-[260px]" data-testid="rb-project-filter">
            <option value="">Select Project</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.pi_number} — {p.client_name}</option>)}
          </select>
        </div>

        {loading && <div className="text-center py-8 text-zinc-500">Loading preview...</div>}

        {/* Step indicators */}
        {preview && !loading && (
          <div className="flex items-center gap-2 mb-4">
            {['preview', 'confirm', 'done'].map((s, i) => (
              <React.Fragment key={s}>
                <span className={`text-xs font-semibold px-3 py-1 ${step === s ? 'bg-blue-700 text-white' : 'bg-zinc-200 text-zinc-600'}`}>
                  {i + 1}. {s === 'preview' ? 'Preview' : s === 'confirm' ? 'Confirm' : 'Done'}
                </span>
                {i < 2 && <span className="text-zinc-300">→</span>}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Summary Cards */}
        {preview && !loading && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="rb-summary">
            <SC label="Previous Bills" value={preview.previous_bill_count} />
            <SC label="Previously Billed" value={fmtCur(preview.total_previous_billed)} />
            <SC label="Current Bill" value={fmtCur(step === 'preview' ? selectedTotal : preview.total_current_bill)} color="text-blue-700" bg="bg-blue-50" />
            <SC label="Cumulative" value={fmtCur(preview.total_previous_billed + selectedTotal)} />
            <SC label="VOs Included" value={preview.vo_count} />
          </div>
        )}

        {/* STEP: PREVIEW */}
        {step === 'preview' && preview && !loading && (
          <>
            {preview.preview_lines.length === 0 ? (
              <div className="bg-white border border-zinc-200 p-8 text-center" data-testid="rb-empty">
                <Receipt size={40} className="mx-auto text-zinc-300 mb-2" />
                <p className="text-sm text-zinc-500">No billable amount. All work has been previously billed.</p>
              </div>
            ) : (
              <>
                <div className="bg-white border border-zinc-200 overflow-x-auto mb-4">
                  <table className="w-full text-sm" data-testid="rb-preview-table">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr>
                        {['', 'Description', 'Trade', 'Unit', 'BOQ Qty', 'Rate', 'Completed', 'Completed Value', 'Prev Billed', 'Current Bill', 'Source'].map(h => (
                          <th key={h} className="px-2 py-2 text-left text-[10px] font-bold uppercase text-zinc-500 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview_lines.map((l, idx) => (
                        <tr key={idx} className={`border-b border-zinc-100 ${selected.has(idx) ? 'bg-blue-50/40' : 'hover:bg-zinc-50'}`} data-testid={`rb-row-${idx}`}>
                          <td className="px-2 py-2"><input type="checkbox" checked={selected.has(idx)} onChange={() => toggleLine(idx)} data-testid={`rb-check-${idx}`} /></td>
                          <td className="px-2 py-2 text-xs font-medium text-zinc-800 max-w-[180px] truncate">{l.description}</td>
                          <td className="px-2 py-2 text-xs text-zinc-600">{l.trade_name}</td>
                          <td className="px-2 py-2 text-xs text-zinc-500">{l.unit}</td>
                          <td className="px-2 py-2 text-xs text-right">{fmtQty(l.boq_qty)}</td>
                          <td className="px-2 py-2 text-xs text-right">{fmtCur(l.rate)}</td>
                          <td className="px-2 py-2 text-xs font-bold text-right">{fmtQty(l.completed_qty)}</td>
                          <td className="px-2 py-2 text-xs text-right">{fmtCur(l.completed_value)}</td>
                          <td className="px-2 py-2 text-xs text-zinc-500 text-right">{fmtCur(l.previous_billed)}</td>
                          <td className="px-2 py-2 text-xs font-bold text-blue-700 text-right">{fmtCur(l.current_bill)}</td>
                          <td className="px-2 py-2">
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 ${l.source_type === 'vo' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                              {l.source_type === 'vo' ? l.vo_number || 'VO' : 'BOQ'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-600">{selected.size} items selected — <b className="text-blue-700">{fmtCur(selectedTotal)}</b></span>
                  <Button onClick={handleConfirm} disabled={selected.size === 0} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="rb-proceed-confirm">
                    <Eye size={18} className="mr-1.5" /> Proceed to Confirm
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {/* STEP: CONFIRM */}
        {step === 'confirm' && preview && (
          <div className="bg-white border border-zinc-200 p-6" data-testid="rb-confirm-view">
            <h3 className="text-lg font-bold text-zinc-800 mb-1">{preview.bill_number_hint}</h3>
            <p className="text-sm text-zinc-600 mb-4">Review and confirm the running bill below.</p>

            <div className="bg-amber-50 border border-amber-200 p-3 mb-4 flex items-start gap-2">
              <Warning size={18} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">This will create a Draft bill for <b>{fmtCur(selectedTotal)}</b> with {selected.size} line items. The bill can be edited before submitting for approval.</p>
            </div>

            <div className="bg-zinc-50 border border-zinc-200 overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead className="bg-zinc-100">
                  <tr>
                    {['Description', 'Qty', 'Rate', 'Amount'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-[10px] font-bold uppercase text-zinc-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...selected].map(i => preview.preview_lines[i]).map((l, idx) => (
                    <tr key={idx} className="border-b border-zinc-100">
                      <td className="px-3 py-2 text-xs font-medium text-zinc-800">{l.description}</td>
                      <td className="px-3 py-2 text-xs text-right">{fmtQty(l.completed_qty)} {l.unit}</td>
                      <td className="px-3 py-2 text-xs text-right">{fmtCur(l.rate)}</td>
                      <td className="px-3 py-2 text-xs font-bold text-blue-700 text-right">{fmtCur(l.current_bill)}</td>
                    </tr>
                  ))}
                  <tr className="bg-zinc-100 font-bold">
                    <td colSpan={3} className="px-3 py-2 text-xs text-right">Total</td>
                    <td className="px-3 py-2 text-xs text-blue-700 text-right">{fmtCur(selectedTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={() => setStep('preview')} variant="outline" className="rounded-none border-zinc-300">Back to Preview</Button>
              <Button onClick={handleGenerate} disabled={generating} className="bg-green-700 hover:bg-green-800 text-white rounded-none" data-testid="rb-confirm-generate">
                <Check size={18} className="mr-1.5" /> {generating ? 'Generating...' : 'Confirm & Generate Bill'}
              </Button>
            </div>
          </div>
        )}

        {/* STEP: DONE */}
        {step === 'done' && generatedBill && (
          <div className="bg-green-50 border border-green-200 p-8 text-center" data-testid="rb-done">
            <Check size={48} className="mx-auto text-green-600 mb-3" />
            <h3 className="text-lg font-bold text-green-800">Bill Generated Successfully</h3>
            <p className="text-sm text-green-700 mt-1">{generatedBill.bill_number} — {fmtCur(generatedBill.bill_amount)}</p>
            <p className="text-xs text-green-600 mt-1">Status: Draft. Navigate to Financials tab to submit for approval.</p>
            <div className="flex items-center gap-3 justify-center mt-4">
              <Button onClick={() => navigate(`/projects/${filterProject}?tab=financials`)} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="rb-go-financials">View in Financials</Button>
              <Button onClick={fetchPreview} variant="outline" className="rounded-none border-zinc-300">Generate Another</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const SC = ({ label, value, color = 'text-zinc-900', bg = 'bg-white' }) => (
  <div className={`${bg} border border-zinc-200 px-3 py-2.5`}>
    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</p>
    <p className={`text-lg font-bold ${color}`}>{value}</p>
  </div>
);

export default RunningBillGenerator;
